/**
 * Sidebar Navigation Component
 * V2 - Updated for 35-resource granular permission system
 *
 * Responsive sidebar with LifeScribe branding and navigation.
 * Uses the 35-resource granular permission system from permissionResources.ts
 */

import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Box,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  FactCheck as FactCheckIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Mail as MailIcon,
  Send as SendIcon,
  FamilyRestroom as FamilyIcon,
  CloudQueue as CloudIcon,
  Psychology as AIIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  MonitorHeart as MonitorIcon,
  AttachMoney as MoneyIcon,
  ExpandLess,
  ExpandMore,
  LocationOn as LocationIcon,
  Style as StyleIcon,
  Mood as MoodIcon,
  AdminPanelSettings as AdminIcon,
  Shield as ShieldIcon,
  AccountBalance as AccountBalanceIcon,
  Campaign as CampaignIcon,
  Movie as MovieIcon,
  TrackChanges as TrackChangesIcon,
  Speed as SpeedIcon,
  HeadsetMic as HeadsetMicIcon,
  PersonOff as PersonOffIcon,
  AccountTree as AccountTreeIcon,
  EmojiEvents as EmojiEventsIcon,
  SmartToy as SmartToyIcon,
  Hub as HubIcon,
  GridOn as GridOnIcon,
  Tune as TuneIcon,
  Quiz as QuizIcon,
  Insights as InsightsIcon,
  Paid as PaidIcon,
  SupportAgent as SupportAgentIcon,
  Balance as BalanceIcon,
  BuildCircle as BuildCircleIcon,
  Layers as LayersIcon,
  OutlinedFlag as OutlinedFlagIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Collections as CollectionsIcon,
  Palette as PaletteIcon,
  // LifeAdvice section
  Lightbulb as LightbulbIcon,
  Category as CategoryIcon,
  LiveHelp as LiveHelpIcon,
  MenuBook as MenuBookIcon,
  HistoryEdu as HistoryEduIcon,
  QueryStats as QueryStatsIcon,
  // Wedding Books section
  FavoriteBorder as FavoriteBorderIcon,
  AutoStories as AutoStoriesIcon,
  // Briefing (daily founder briefing published by the Cowork task)
  Article as ArticleIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import { setCurrentPage, setBreadcrumbs } from "../../store/uiSlice";
import { NavigationItem, BreadcrumbItem } from "../../types/dashboard";
import {
  Permission,
  hasAccess,
  countFullAccess,
  SUPER_ADMIN_EMAILS,
} from "../../config/permissionResources";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;
// v2: saved state is merged OVER the defaults, so a stored v1 map would keep
// Finance/Content expanded forever. Bumping the key retires those maps once and
// lets the Quill-only defaults below actually apply.
const SIDEBAR_EXPANDED_STORAGE_KEY = "lsadmin.sidebar.expanded.v3";
// v2 saved "ad-performance: true" for everyone (it used to default open), which
// would override the new collapsed default. v2 state is carried over once, minus
// that one entry.
const SIDEBAR_EXPANDED_LEGACY_KEY = "lsadmin.sidebar.expanded.v2";
const SIDEBAR_LEGACY_DROP = ["ad-performance"];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
}

// Navigation configuration with granular permission system and collapsible groups
// Each item now uses 'requiredResource' to specify which permission resource is needed
// Items with isGroup: true render as collapsible gold-dot group headers (no requiredResource
// of their own — visibility is derived from whether any descendant leaf is accessible)
const navigationItems: NavigationItem[] = [
  // First, and the landing page after sign-in. It has its own page AND a
  // submenu: clicking the label opens /ads and expands it, the chevron only
  // toggles (see renderNavigationItem).
  {
    id: "ad-performance",
    label: "Ad Dashboard",
    icon: "CampaignIcon",
    path: "/ads",
    requiredResource: "subscription_analytics",
    children: [
      {
        id: "ad-report",
        label: "SVD Ad Report",
        icon: "ArticleIcon",
        path: "/ad-report",
        requiredResource: "subscription_analytics",
      },
    ],
  },
  // Second: Grokbot (Bot Operations), between the Ad Dashboard and the Overview.
  // Super admin only in practice (resource bot_operations has no default grant).
  // matchPrefix keeps it highlighted on /dashboard/bot-ops/bots/:slug and friends.
  {
    id: "grokbot",
    label: "Grokbot",
    icon: "HubIcon",
    path: "/dashboard/bot-ops",
    requiredResource: "bot_operations",
    matchPrefix: true,
  },
  // Third. Its data is prefetched at sign-in (services/overviewPrefetch.ts), so
  // it opens without the 30-second load.
  {
    id: "overview",
    label: "Overview",
    icon: "DashboardIcon",
    path: "/dashboard",
    requiredResource: "overview",
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // FINANCE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-finance",
    label: "Finance",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "revenue-subscriptions",
        label: "Revenue & Subscriptions",
        icon: "TrendingUpIcon",
        path: "/dashboard/revenue-subscriptions",
        requiredResources: ["revenue_analytics", "subscription_analytics"],
      },
      {
        id: "cost-overview",
        label: "Grok-AWS-Stripe Costs",
        icon: "MonitorIcon",
        path: "/dashboard/cost-management/overview",
        requiredResource: "cost_management",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICS & INSIGHTS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-analytics",
    label: "Analytics & Insights",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "mmp-analytics",
        label: "User Activation Funnel",
        icon: "AnalyticsIcon",
        path: "/dashboard/mmp-analytics",
        requiredResource: "subscription_analytics",
      },
      {
        id: "notification-journey-tracking",
        label: "Push & In-App",
        icon: "NotificationsIcon",
        path: "/dashboard/notification-journey",
        requiredResource: "subscription_analytics",
      },
      {
        id: "website-analytics",
        label: "Website Traffic",
        icon: "AnalyticsIcon",
        path: "/dashboard/website-analytics",
        requiredResource: "subscription_analytics",
      },
      {
        id: "google-ads-landing",
        label: "Google Ads",
        icon: "CampaignIcon",
        path: "/dashboard/google-ads-landing",
        requiredResource: "subscription_analytics",
      },
      {
        id: "store-clicks",
        label: "Store Clicks",
        icon: "TrendingUpIcon",
        path: "/dashboard/store-clicks",
        requiredResource: "customer_analytics",
      },
      {
        id: "survey-tracking",
        label: "Survey Tracking",
        icon: "QuizIcon",
        path: "/dashboard/survey-tracking",
        requiredResource: "audience_manager",
      },
      {
        id: "product-intelligence",
        label: "Product Intelligence",
        icon: "TrackChangesIcon",
        path: "/dashboard/product-intelligence",
        requiredResource: "product_intelligence",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // MEMBERS & CONVERSION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-members",
    label: "Members & Conversion",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "customer-support",
        label: "Customer Support",
        icon: "HeadsetMicIcon",
        path: "/dashboard/customer-support",
        requiredResource: "customer_support",
      },
      {
        id: "user-management",
        label: "User Management",
        icon: "PeopleIcon",
        path: "/dashboard/users",
        requiredResource: "user_management",
      },
      {
        id: "free-tier-users",
        label: "Free Tier Members",
        icon: "TrendingUpIcon",
        path: "/dashboard/free-tier",
        requiredResource: "free_tier_users",
      },
      {
        id: "test-account-review",
        label: "Test Account Review",
        icon: "FactCheckIcon",
        path: "/dashboard/test-accounts",
        requiredResource: "user_management",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // MARKETING EMAILS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-marketing",
    label: "Marketing Emails",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "marketing-overview",
        label: "Marketing Overview",
        icon: "TrackChangesIcon",
        path: "/dashboard/communications/marketing/overview",
        requiredResource: "comms_overview",
      },
      {
        id: "marketing-funnels",
        label: "Funnel Manager",
        icon: "AccountTreeIcon",
        path: "/dashboard/communications/marketing/funnels",
        requiredResource: "free_tier_users",
      },
      {
        id: "marketing-templates",
        label: "Marketing Templates",
        icon: "MailIcon",
        path: "/dashboard/communications/marketing/templates",
        requiredResource: "email_templates",
      },
      {
        id: "marketing-audiences",
        label: "Audiences",
        icon: "PeopleIcon",
        path: "/dashboard/communications/marketing/audiences",
        requiredResource: "audience_manager",
      },
      {
        // Merged "Campaigns" + "Ad-hoc Composer": one compose surface (send now / schedule).
        id: "marketing-compose",
        label: "Compose & Send",
        icon: "SendIcon",
        path: "/dashboard/communications/marketing/compose",
        requiredResource: "email_campaigns",
      },
      {
        // Limited marketing triggers (nurture sequences live in the Conversion & Nurture Engine;
        // transactional triggers live in Communications).
        id: "marketing-triggers",
        label: "Marketing Triggers",
        icon: "SettingsIcon",
        path: "/dashboard/communications/marketing/triggers",
        requiredResource: "email_triggers",
      },
      {
        id: "promotions",
        label: "Promotions & Discounts",
        icon: "MoneyIcon",
        path: "/dashboard/promotions",
        requiredResource: "stripe_discounts",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // TRANSACTIONAL EMAILS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-transactional",
    label: "Transactional Emails",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "transactional-overview",
        label: "Transactional Overview",
        icon: "SecurityIcon",
        path: "/dashboard/communications/transactional/overview",
        requiredResource: "comms_overview",
      },
      {
        id: "transactional-templates",
        label: "Transactional Templates",
        icon: "MailIcon",
        path: "/dashboard/communications/transactional/templates",
        requiredResource: "comms_overview",
      },
      {
        id: "transactional-triggers",
        label: "Transactional Triggers",
        icon: "SettingsIcon",
        path: "/dashboard/communications/transactional/triggers",
        requiredResource: "comms_overview",
      },
    ],
  },
  // ── OPERATE — Live Surveillance ───────────────────────────────────────────
  {
    id: "group-operate",
    label: "Operate",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "operate-live-stream",
        label: "Live Activity Stream",
        icon: "NotificationsIcon",
        path: "/dashboard/communications/operate/live",
        requiredResource: "comms_overview",
      },
      {
        id: "operate-scheduled-queue",
        label: "Scheduled Sends Queue",
        icon: "SpeedIcon",
        path: "/dashboard/communications/operate/queue",
        requiredResource: "comms_overview",
      },
    ],
  },
  // ── MEASURE — Analytics ───────────────────────────────────────────────────
  {
    id: "group-measure",
    label: "Measure",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "measure-overview",
        label: "Overview",
        icon: "AnalyticsIcon",
        path: "/dashboard/communications/measure/overview",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-funnels",
        label: "Funnel Performance",
        icon: "AccountTreeIcon",
        path: "/dashboard/communications/measure/funnels",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-campaigns",
        label: "Campaign Performance",
        icon: "CampaignIcon",
        path: "/dashboard/communications/measure/campaigns",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-audiences",
        label: "Audience Insights",
        icon: "PeopleIcon",
        path: "/dashboard/communications/measure/audiences",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-templates",
        label: "Template Performance",
        icon: "MailIcon",
        path: "/dashboard/communications/measure/templates",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-triggers",
        label: "Trigger Health",
        icon: "SettingsIcon",
        path: "/dashboard/communications/measure/triggers",
        requiredResource: "comms_overview",
      },
      {
        id: "measure-deliverability",
        label: "Deliverability Health",
        icon: "MonitorIcon",
        path: "/dashboard/communications/measure/deliverability",
        requiredResource: "comms_overview",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // ADMINISTRATION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-administration",
    label: "Administration",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "admin-user-management",
        label: "Admin User Management",
        icon: "AdminIcon",
        path: "/dashboard/admin-users",
        requiredResource: "admin_users",
      },
      {
        // Self-service 2FA — no requiredResource so every authenticated admin can manage their own
        id: "security-mfa",
        label: "Security (2FA)",
        icon: "AdminIcon",
        path: "/dashboard/security",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // CONTENT
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-content",
    label: "Content",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "content",
        label: "Content Management",
        icon: "StyleIcon",
        path: "/dashboard/content",
        requiredResource: "chapter_creation",
        children: [
          {
            id: "chapter-creation",
            label: "Chapter Creation",
            icon: "StyleIcon",
            path: "/dashboard/content/chapter-creation",
            requiredResource: "chapter_creation",
          },
          {
            id: "quest-creation",
            label: "Quest Creation",
            icon: "EmojiEventsIcon",
            path: "/dashboard/content/quest-creation",
            requiredResource: "quest_creation",
          },
          {
            id: "legacy-quests",
            label: "Legacy Quests",
            icon: "AIIcon",
            path: "/dashboard/content/legacy-quests",
            requiredResource: "legacy_quests",
          },
          {
            id: "daily-inspirations",
            label: "Daily Inspirations",
            icon: "MoodIcon",
            path: "/dashboard/content/daily-inspirations",
            requiredResource: "daily_inspirations",
          },
          {
            id: "pdf-templates",
            label: "PDF Templates",
            icon: "StyleIcon",
            path: "/dashboard/content/pdf-templates",
            requiredResource: "pdf_templates",
          },
        ],
      },
      {
        id: "ai-section",
        label: "AI & Styles",
        icon: "AIIcon",
        path: "/dashboard/ai",
        requiredResource: "ai_styles",
        children: [
          {
            id: "ai-styles",
            label: "AI Styles",
            icon: "StyleIcon",
            path: "/dashboard/content/ai-styles",
            requiredResource: "ai_styles",
          },
          {
            id: "ai-moods",
            label: "AI Moods",
            icon: "MoodIcon",
            path: "/dashboard/content/ai-moods",
            requiredResource: "ai_moods",
          },
          {
            id: "brain-health",
            label: "Brain Health V5.0",
            icon: "MonitorIcon",
            path: "/dashboard/brain-health",
            requiredResource: "brain_health",
          },
          {
            id: "grok-imagine",
            label: "Grok Imagine Video",
            icon: "MovieIcon",
            path: "/dashboard/grok-imagine",
            requiredResource: "grok_imagine",
          },
        ],
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // QUILL
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-quill",
    label: "Quill",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "quill-models",
        label: "AI Models",
        icon: "SmartToyIcon",
        path: "/dashboard/quill/models",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-pipeline-models",
        label: "Pipeline Models",
        icon: "AccountTreeIcon",
        path: "/dashboard/quill/pipeline-models",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-ai-styles",
        label: "AI Styles",
        icon: "PaletteIcon",
        path: "/dashboard/quill/ai-styles",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-weights",
        label: "Weight Matrix",
        icon: "GridOnIcon",
        path: "/dashboard/quill/weights",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-reasoning",
        label: "Reasoning Prompts",
        icon: "TuneIcon",
        path: "/dashboard/quill/reasoning",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-guardrails",
        label: "Guardrails",
        icon: "ShieldIcon",
        path: "/dashboard/quill/guardrails",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-interview",
        label: "Interview Bank",
        icon: "QuizIcon",
        path: "/dashboard/quill/interview",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-coverage",
        label: "Coverage",
        icon: "TrackChangesIcon",
        path: "/dashboard/quill/coverage",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-fidelity",
        label: "Fidelity",
        icon: "InsightsIcon",
        path: "/dashboard/quill/fidelity",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-costs",
        label: "AI Costs",
        icon: "PaidIcon",
        path: "/dashboard/quill/costs",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-agents",
        label: "Agents",
        icon: "SupportAgentIcon",
        path: "/dashboard/quill/agents",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-mirror-guardian",
        label: "Mirror Guardian",
        icon: "BalanceIcon",
        path: "/dashboard/quill/mirror-guardian",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-operations",
        label: "Operations",
        icon: "BuildCircleIcon",
        path: "/dashboard/quill/operations",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-dimensions",
        label: "Dimensions",
        icon: "LayersIcon",
        path: "/dashboard/quill/dimensions",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-flags",
        label: "Feature Flags",
        icon: "OutlinedFlagIcon",
        path: "/dashboard/quill/flags",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-voice-consent",
        label: "Voice & Consent",
        icon: "RecordVoiceOverIcon",
        path: "/dashboard/quill/voice-consent",
        requiredResource: "quill_admin",
      },
      {
        id: "quill-photo-memories",
        label: "Photo Memories",
        icon: "CollectionsIcon",
        path: "/dashboard/quill/photo-memories",
        requiredResource: "quill_admin",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // LIFEADVICE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-lifeadvice",
    label: "LifeAdvice",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "lifeadvice-overview",
        // Not plain "Overview" — the top-level dashboard item already owns that
        // label, and two identical rail entries are ambiguous.
        label: "Advice Overview",
        icon: "LightbulbIcon",
        path: "/dashboard/lifeadvice/overview",
        requiredResource: "lifeadvice_admin",
      },
      {
        id: "lifeadvice-pillars",
        label: "Wisdom Pillars",
        icon: "CategoryIcon",
        path: "/dashboard/lifeadvice/pillars",
        requiredResource: "lifeadvice_admin",
      },
      {
        id: "lifeadvice-prompts",
        label: "Prompts",
        icon: "LiveHelpIcon",
        path: "/dashboard/lifeadvice/prompts",
        requiredResource: "lifeadvice_admin",
      },
      {
        id: "lifeadvice-curated",
        label: "Curated Library",
        icon: "MenuBookIcon",
        path: "/dashboard/lifeadvice/curated",
        requiredResource: "lifeadvice_admin",
      },
      {
        id: "lifeadvice-entries",
        label: "Member Entries",
        icon: "HistoryEduIcon",
        path: "/dashboard/lifeadvice/entries",
        requiredResource: "lifeadvice_admin",
      },
      {
        id: "lifeadvice-analytics",
        label: "Analytics",
        icon: "QueryStatsIcon",
        path: "/dashboard/lifeadvice/analytics",
        requiredResource: "lifeadvice_admin",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // WEDDING BOOKS
  //
  // Eight leaves. Guests, invitations and chapters are meaningful PER BOOK
  // (a cross-book guest list would be ten thousand rows nobody reads), so they
  // live inside Book Detail, which is reached from a row click at
  // /dashboard/wedding/books/:bookId and therefore has no rail entry.
  //
  // The slugs here are cross-checked against WeddingSection.tsx's <Route path>
  // literals by components/wedding/__e2e__/navigation.journey.test.tsx.
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-wedding",
    label: "Wedding Books",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "wedding-overview",
        label: "Overview",
        icon: "FavoriteBorderIcon",
        path: "/dashboard/wedding/overview",
        requiredResource: "wedding_books",
      },
      {
        id: "wedding-books",
        label: "Books",
        icon: "AutoStoriesIcon",
        path: "/dashboard/wedding/books",
        requiredResource: "wedding_books",
      },
      {
        // The Wedding Book's "users" are couples, and their commercial state
        // is the tier of a book they own rather than a subscription — which is
        // why the tier control lives on a book row inside this page and not on
        // the couple. See weddingAdmin.js's COUPLES section header.
        id: "wedding-couples",
        label: "Couples",
        icon: "PeopleIcon",
        path: "/dashboard/wedding/couples",
        requiredResource: "wedding_books",
      },
      {
        // Real-time control of what coordinators are offered. Creating an item
        // makes it OFFERED; its APPEARANCE is still CSS compiled into two
        // container images, which the page states beside its create button.
        id: "wedding-catalogue",
        label: "Templates & Styles",
        icon: "PaletteIcon",
        path: "/dashboard/wedding/catalogue",
        requiredResource: "wedding_books",
      },
      {
        // Separate from Revenue on purpose: Revenue is windowed money-in from
        // couples, this is an all-time people-list of members-out to
        // LifeScribe. One page cannot hold both range semantics honestly.
        id: "wedding-conversions",
        label: "Guest Conversions",
        icon: "InsightsIcon",
        path: "/dashboard/wedding/conversions",
        requiredResource: "wedding_books",
      },
      {
        id: "wedding-revenue",
        label: "Revenue & Conversions",
        icon: "PaidIcon",
        path: "/dashboard/wedding/revenue",
        requiredResource: "wedding_books",
      },
      {
        // The DB-backed template registry. Publishing here makes a template
        // selectable in every coordinator dashboard the same minute — it does
        // NOT send anything, which is why the page says so three times.
        id: "wedding-templates",
        label: "Email Templates",
        icon: "MailIcon",
        path: "/dashboard/wedding/templates",
        requiredResource: "wedding_books",
      },
      {
        // Couples, coordinators and converted guests in one list. Distinct
        // from Couples (which is only the buyers) and from Guest Conversions
        // (which is only the cross-sell).
        id: "wedding-members",
        label: "Members",
        icon: "PeopleIcon",
        path: "/dashboard/wedding/members",
        requiredResource: "wedding_books",
      },
      {
        // READ-ONLY. Migration 450's moderation column had no admin surface at
        // all; this one reads it and does not write it. The state machine is
        // the couple's, photo and audio 'pending' is structural rather than a
        // queue, and admin has no playback path — so there is no approve or
        // hide control here and the page says so in the server's own words.
        id: "wedding-moderation",
        label: "Media Moderation",
        icon: "CollectionsIcon",
        path: "/dashboard/wedding/moderation",
        requiredResource: "wedding_books",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: "group-system",
    label: "System",
    icon: "",
    path: "",
    isGroup: true,
    children: [
      {
        id: "monitoring",
        label: "System Monitoring",
        icon: "MonitorIcon",
        path: "/dashboard/monitoring",
        requiredResource: "aws_cloudwatch",
        children: [
          {
            id: "aws-monitor",
            label: "AWS CloudWatch",
            icon: "CloudIcon",
            path: "/dashboard/monitoring/aws",
            requiredResource: "aws_cloudwatch",
          },
          {
            id: "website-monitor",
            label: "Website Status",
            icon: "LocationIcon",
            path: "/dashboard/monitoring/website",
            requiredResource: "website_status",
          },
          {
            id: "api-health",
            label: "API Health",
            icon: "SpeedIcon",
            path: "/dashboard/monitoring/api",
            requiredResource: "api_health",
          },
          {
            id: "ecs-monitor",
            label: "ECS Monitor",
            icon: "CloudIcon",
            path: "/dashboard/monitoring/ecs",
            requiredResource: "ecs_monitor",
          },
        ],
      },
      {
        id: "audit-log",
        label: "Audit Log",
        icon: "SecurityIcon",
        path: "/dashboard/system/audit",
        requiredResource: "audit_log",
      },
    ],
  },
];

// Icon mapping - ensure all navigation icons are mapped
const iconMap: Record<string, React.ComponentType> = {
  DashboardIcon: DashboardIcon,
  ArticleIcon: ArticleIcon,
  TrendingUpIcon: TrendingUpIcon,
  FactCheckIcon: FactCheckIcon,
  PeopleIcon: PeopleIcon,
  NotificationsIcon: NotificationsIcon,
  EmailIcon: EmailIcon,
  SmsIcon: SmsIcon,
  MailIcon: MailIcon,
  SendIcon: SendIcon,
  FamilyIcon: FamilyIcon,
  CloudIcon: CloudIcon,
  AIIcon: AIIcon,
  AnalyticsIcon: AnalyticsIcon,
  SettingsIcon: SettingsIcon,
  SecurityIcon: SecurityIcon,
  MonitorIcon: MonitorIcon,
  MoneyIcon: MoneyIcon,
  LocationIcon: LocationIcon,
  StyleIcon: StyleIcon,
  MoodIcon: MoodIcon,
  AdminIcon: AdminIcon,
  MovieIcon: MovieIcon,
  TrackChangesIcon: TrackChangesIcon,
  SpeedIcon: SpeedIcon,
  CampaignIcon: CampaignIcon,
  HeadsetMicIcon: HeadsetMicIcon,
  PersonOffIcon: PersonOffIcon,
  AccountTreeIcon: AccountTreeIcon,
  ShieldIcon: ShieldIcon,
  EmojiEventsIcon: EmojiEventsIcon,
  SmartToyIcon: SmartToyIcon,
  HubIcon: HubIcon,
  GridOnIcon: GridOnIcon,
  TuneIcon: TuneIcon,
  QuizIcon: QuizIcon,
  InsightsIcon: InsightsIcon,
  PaidIcon: PaidIcon,
  SupportAgentIcon: SupportAgentIcon,
  BalanceIcon: BalanceIcon,
  BuildCircleIcon: BuildCircleIcon,
  LayersIcon: LayersIcon,
  OutlinedFlagIcon: OutlinedFlagIcon,
  RecordVoiceOverIcon: RecordVoiceOverIcon,
  CollectionsIcon: CollectionsIcon,
  PaletteIcon: PaletteIcon,
  LightbulbIcon: LightbulbIcon,
  CategoryIcon: CategoryIcon,
  LiveHelpIcon: LiveHelpIcon,
  MenuBookIcon: MenuBookIcon,
  HistoryEduIcon: HistoryEduIcon,
  QueryStatsIcon: QueryStatsIcon,
  FavoriteBorderIcon: FavoriteBorderIcon,
  AutoStoriesIcon: AutoStoriesIcon,
};

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  collapsed,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { currentPage } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);

  // Quill and LifeAdvice are the groups open on a fresh load; every other starts
  // collapsed so the rail stays short. (The active page's ancestor group still
  // auto-expands, so you never land on a hidden item.)
  const DEFAULT_EXPANDED: Record<string, boolean> = {
    // Collapsed by default: the SVD Ad Report submenu stays out of the way, so
    // the sidebar reads Ad Dashboard / Grokbot / Overview.
    "ad-performance": false,
    "group-quill": true,
    "group-lifeadvice": true,
    "group-finance": false,
    "group-content": false,
    content: false,
    "ai-section": false,
    monitoring: false,
  };

  const [expandedItems, setExpandedItems] = React.useState<
    Record<string, boolean>
  >(() => {
    try {
      const raw = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return { ...DEFAULT_EXPANDED, ...parsed };
        }
      } else {
        const legacy = window.localStorage.getItem(SIDEBAR_EXPANDED_LEGACY_KEY);
        if (legacy) {
          const parsed = JSON.parse(legacy);
          if (parsed && typeof parsed === "object") {
            SIDEBAR_LEGACY_DROP.forEach((k) => { delete parsed[k]; });
            return { ...DEFAULT_EXPANDED, ...parsed };
          }
        }
      }
    } catch {
      // Malformed/unavailable localStorage — fall back to defaults
    }
    return DEFAULT_EXPANDED;
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_EXPANDED_STORAGE_KEY,
        JSON.stringify(expandedItems),
      );
    } catch {
      // Ignore storage failures (private mode / quota exceeded)
    }
  }, [expandedItems]);

  // Map of item id -> array of ancestor ids (outermost first), built once from the
  // static navigationItems tree. Used to auto-expand groups for the active page and
  // to build breadcrumbs.
  const ancestorIndex = React.useMemo(() => {
    const index: Record<string, string[]> = {};
    const walk = (items: NavigationItem[], ancestors: string[]) => {
      items.forEach((item) => {
        index[item.id] = ancestors;
        if (item.children && item.children.length > 0) {
          walk(item.children, [...ancestors, item.id]);
        }
      });
    };
    walk(navigationItems, []);
    return index;
  }, []);

  const labelIndex = React.useMemo(() => {
    const index: Record<string, string> = {};
    const walk = (items: NavigationItem[]) => {
      items.forEach((item) => {
        index[item.id] = item.label;
        if (item.children && item.children.length > 0) {
          walk(item.children);
        }
      });
    };
    walk(navigationItems);
    return index;
  }, []);

  // Auto-expand the ancestor group(s) of whichever page is active. Only ever sets
  // groups to expanded — never collapses a group the user opened themselves.
  React.useEffect(() => {
    const ancestors = ancestorIndex[currentPage];
    if (!ancestors || ancestors.length === 0) return;

    setExpandedItems((prev) => {
      let changed = false;
      const next = { ...prev };
      ancestors.forEach((ancestorId) => {
        if (!next[ancestorId]) {
          next[ancestorId] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [currentPage, ancestorIndex]);

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // id -> item, for every item with a page of its own.
  const itemIndex = React.useMemo(() => {
    const index: Record<string, NavigationItem> = {};
    const walk = (items: NavigationItem[]) => {
      items.forEach((item) => {
        if (item.path) index[item.id] = item;
        if (item.children && item.children.length > 0) walk(item.children);
      });
    };
    walk(navigationItems);
    return index;
  }, []);

  const buildBreadcrumbs = (item: NavigationItem): BreadcrumbItem[] => [
    // "/" rather than "/dashboard": the root follows HomeRedirect, which sends
    // each user to their landing page.
    { label: "Dashboard", path: "/" },
    ...(ancestorIndex[item.id] || [])
      .filter((ancestorId) => labelIndex[ancestorId])
      .map((ancestorId) => {
        // An ancestor that is a page itself (Ad Dashboard) is a link.
        const ancestor = itemIndex[ancestorId];
        return ancestor ? { label: ancestor.label, path: ancestor.path } : { label: labelIndex[ancestorId] };
      }),
    { label: item.label, path: item.path },
  ];

  // Follow the URL, not just sidebar clicks. currentPage used to change only on
  // a click, so landing anywhere directly — which every sign-in now does, on
  // /ads — left "Overview" highlighted from the initial state.
  React.useEffect(() => {
    const items = Object.values(itemIndex);
    const underPath = (item: NavigationItem) =>
      location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
    // Exact match first; otherwise the deepest item that opted in to prefix matching.
    const match =
      items.find((item) => item.path === location.pathname) ||
      items
        .filter((item) => item.matchPrefix && underPath(item))
        .sort((a, b) => b.path.length - a.path.length)[0];
    if (!match) return;
    if (match.id !== currentPage) dispatch(setCurrentPage(match.id));
    dispatch(setBreadcrumbs(buildBreadcrumbs(match)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, itemIndex]);

  const handleNavigate = (item: NavigationItem) => {
    dispatch(setCurrentPage(item.id));
    dispatch(setBreadcrumbs(buildBreadcrumbs(item)));

    // Navigate using React Router
    navigate(item.path);

    if (isMobile) {
      onClose();
    }
  };

  /**
   * Check if the current user is a hardcoded super admin
   * These users have all permissions regardless of their permissions array
   */
  const isSuperAdmin = (): boolean => {
    if (!user?.email) return false;
    return SUPER_ADMIN_EMAILS.includes(user.email.toLowerCase());
  };

  /**
   * Check if the current user can access a navigation item
   * Uses the new granular permission system from permissionResources.ts
   */
  const checkNavItemAccess = (item: NavigationItem): boolean => {
    if (!user) return false;

    // Hardcoded super admins can access everything
    if (isSuperAdmin()) return true;

    // Get user permissions from Redux state
    const userPermissions: Permission[] = user.permissions || [];

    // OR-gated: user must have access to ANY of the listed resources
    if (item.requiredResources && item.requiredResources.length > 0) {
      return item.requiredResources.some(r => hasAccess(userPermissions, r, user.email));
    }

    // If no requiredResource, allow access (backward compatibility)
    if (!item.requiredResource) return true;

    // Check if user has access to the required resource
    return hasAccess(userPermissions, item.requiredResource, user.email);
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent /> : <DashboardIcon />;
  };

  /**
   * A group header renders only if at least one descendant (at any depth) would
   * itself be visible to the current user.
   */
  const groupHasVisibleChildren = (item: NavigationItem): boolean => {
    if (!item.children || item.children.length === 0) return false;
    return item.children.some((child) =>
      child.isGroup
        ? groupHasVisibleChildren(child)
        : checkNavItemAccess(child),
    );
  };

  // Flatten isGroup wrappers into their direct children for the collapsed rail —
  // groups themselves render nothing when collapsed, only their (icon-only) children do.
  // A page with its own submenu (Ad Dashboard) flattens to itself plus its
  // children, because children never render on the collapsed rail and the SVD
  // report would otherwise be unreachable there.
  const getRailItems = (items: NavigationItem[]): NavigationItem[] =>
    collapsed
      ? items.flatMap((item) => {
          if (item.isGroup) return item.children ?? [];
          if (item.path && item.children && item.children.length > 0) {
            return [{ ...item, children: undefined }, ...item.children];
          }
          return [item];
        })
      : items;

  const renderNavigationItem = (item: NavigationItem, depth = 0) => {
    // Collapsible group header rendering
    if (item.isGroup) {
      if (collapsed) return null;
      if (!groupHasVisibleChildren(item)) return null;

      const isGroupExpanded = expandedItems[item.id];

      return (
        <React.Fragment key={item.id}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => handleToggleExpand(item.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggleExpand(item.id);
              }
            }}
            sx={{
              px: 2.5,
              pt: 2.5,
              pb: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  bgcolor: "#D4AC0D",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontSize: "0.66rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "#5D6D7E",
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                {item.label}
              </Typography>
            </Box>
            {isGroupExpanded ? (
              <ExpandLess sx={{ fontSize: 18, color: "#5D6D7E" }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18, color: "#5D6D7E" }} />
            )}
          </Box>
          <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => renderNavigationItem(child, 0))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    // Use granular permission-based access check
    if (!checkNavItemAccess(item)) {
      return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    const isActive = currentPage === item.id;
    // A page that also has a submenu. Its label opens the page (and the
    // submenu); only the chevron toggles. Without this, nesting the SVD report
    // turned Ad Dashboard into an expand-only header that never opened /ads.
    const isNavigableParent = Boolean(hasChildren && item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => {
              if (isNavigableParent) {
                if (!isExpanded) handleToggleExpand(item.id);
                handleNavigate(item);
              } else if (hasChildren) {
                handleToggleExpand(item.id);
              } else {
                handleNavigate(item);
              }
            }}
            selected={isActive}
            sx={{
              minHeight: 48,
              justifyContent: collapsed ? "center" : "initial",
              px: 2.5,
              ml: depth * 2,
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                borderLeft: "4px solid #D4AC0D",
                paddingLeft: "16px",
                "& .MuiListItemIcon-root": {
                  color: "#D4AC0D",
                },
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 3,
                justifyContent: "center",
                color: isActive ? "inherit" : "text.secondary",
              }}
            >
              {getIconComponent(item.icon)}
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={item.label}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      fontWeight: isActive ? 600 : 400,
                    },
                  }}
                />
                {item.badge && (
                  <Chip
                    label={item.badge.count}
                    color={item.badge.color}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
                {hasChildren && (isNavigableParent ? (
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.label}`}
                    aria-expanded={Boolean(isExpanded)}
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleToggleExpand(item.id); }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); handleToggleExpand(item.id); }
                    }}
                    sx={{ display: "inline-flex", borderRadius: 1, "&:hover": { backgroundColor: "action.hover" } }}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                ) : (isExpanded ? <ExpandLess /> : <ExpandMore />))}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && !collapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) =>
                renderNavigationItem(child, depth + 1),
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo and Brand */}
      <Box
        sx={{
          p: collapsed ? 1 : 3,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          minHeight: 64,
          position: "relative",
          background: "linear-gradient(135deg, #0c3344 0%, #11465c 55%, #1A5F7A 100%)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(to right, #D4AC0D, transparent)",
          },
        }}
      >
        {collapsed ? (
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              color: "#D4AC0D",
            }}
          >
            LS
          </Typography>
        ) : (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                color: "#D4AC0D",
                lineHeight: 1,
              }}
            >
              LifeScribe AI
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(249, 243, 233, 0.72)",
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              Admin Dashboard
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <List sx={{ pt: 2 }}>
          {getRailItems(navigationItems).map((item) =>
            renderNavigationItem(item),
          )}
        </List>
      </Box>

      {/* User Info with Permission Stats */}
      {!collapsed && user && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          {(() => {
            const permissions = user.permissions || [];
            const fullAccessCount = countFullAccess(permissions);
            const isUserSuperAdmin = isSuperAdmin();

            // Color scheme based on access level
            const displayColor = isUserSuperAdmin
              ? "#7C3AED"
              : fullAccessCount > 10
                ? "#10B981"
                : "#6366F1";
            const displayBgColor = isUserSuperAdmin
              ? "rgba(124, 58, 237, 0.08)"
              : fullAccessCount > 10
                ? "rgba(16, 185, 129, 0.08)"
                : "rgba(99, 102, 241, 0.08)";
            const displayBorderColor = isUserSuperAdmin
              ? "rgba(124, 58, 237, 0.3)"
              : fullAccessCount > 10
                ? "rgba(16, 185, 129, 0.3)"
                : "rgba(99, 102, 241, 0.3)";

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: displayBgColor,
                    border: `2px solid ${displayBorderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: displayColor,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {user.name
                    ? user.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                    : user.email?.charAt(0).toUpperCase() || "U"}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {user.name || user.email || "Admin User"}
                  </Typography>
                  <Chip
                    label={
                      isUserSuperAdmin
                        ? "Super Admin"
                        : `${fullAccessCount} Full / ${permissions.length - fullAccessCount} View`
                    }
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      bgcolor: displayBgColor,
                      color: displayColor,
                      border: `1px solid ${displayBorderColor}`,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}
    </Box>
  );

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          background: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          top: isMobile ? 0 : '64px', // Account for TopBar on desktop
          height: isMobile ? '100%' : 'calc(100% - 64px)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
